import { addValueToAIGenerateMessage } from "../redux/aiGeneratedMessage";
import { setCitationsLoading } from "../redux/citationsSlice";
import { ConstantValues } from "./constants";

const ERROR_MESSAGES = {
  NO_RESPONSE:
    "No response generated. There may be an issue with the selected model. Please switch to a different Claude or GPT model below.",
  SUPPORT_MESSAGE:
    "\n\nIf you continue to experience an issue, please try a different LLM or [contact support](mailto:nhhelpu@northhighland.com?subject=AIMS%20Chat%20Error%20Received).",
  LLM_SWITCH_PATTERN: "try switching to a different llm",
};

export async function getChunkAndUpdateAIMessage(
  response,
  readableStream,
  reduxDispatched = null,
  reactSetState = null,
  setLoadingState = null,
  dispatch,
) {
  const reader = readableStream
    .pipeThrough(new TextDecoderStream())
    .getReader();
  let lastMessageValue = null;
  let chartData = null;
  let done, value;
  let sourceDocuments = null;
  let citations = [];
  let webSearchCitations = [];
  let chunks = ConstantValues.EMPTY_STRING;
  let chunkNr = 0;
  let isError = false;

  try {
    while (!done) {
      ({ value, done } = await reader.read());

      if (chunkNr === 0 && setLoadingState) setLoadingState(false);
      chunkNr++;

      if (done) {
        if (setLoadingState) setLoadingState(false);
        break;
      }

      chunks += value ?? ConstantValues.EMPTY_STRING;

      let newMessage;

      // The backend terminates every JSON payload with \n, so a raw \n here is always a frame boundary, never message content.
      const streamLines = chunks.split("\n");
      chunks = streamLines.pop();
      const completeLines = streamLines.filter(
        (streamLine) => streamLine.length > 0,
      );
      if (completeLines.length === 0) continue;
      const lastCompleteLine = completeLines[completeLines.length - 1];

      try {
        newMessage = JSON.parse(lastCompleteLine);
      } catch (e) {
        console.error("Error parsing chunk:", e);
        continue;
      }
      if (newMessage?.error === true) {
        lastMessageValue = newMessage.message;
        isError = true;
        break;
      }

      if (newMessage.chart_data) {
        chartData = newMessage.chart_data;
        newMessage.message += chartData.content;
      }

      newMessage.message = newMessage?.message
        ?.replace(/```/g, ConstantValues.EMPTY_STRING)
        ?.replace(/`/g, ConstantValues.EMPTY_STRING);

      if (newMessage?.citating === true) {
        dispatch(setCitationsLoading(true));
      } else if (newMessage?.citating === false || newMessage?.hasFinished) {
        dispatch(setCitationsLoading(false));
      }

      lastMessageValue = newMessage?.message;

      if (
        !newMessage?.message ||
        newMessage?.message
          .toLowerCase()
          .includes(ERROR_MESSAGES.LLM_SWITCH_PATTERN)
      ) {
        newMessage.message += ERROR_MESSAGES.SUPPORT_MESSAGE;
        newMessage.message = newMessage.message
          .replace(
            /Try switching to a different llm\./gi,
            ConstantValues.EMPTY_STRING,
          )
          .trim();
      }

      if (newMessage?.hasFinished) {
        sourceDocuments = newMessage?.files;
        citations = newMessage?.citations || [];
        webSearchCitations = newMessage?.web_search_citations || [];
      }

      if (reduxDispatched) {
        reduxDispatched(addValueToAIGenerateMessage(newMessage?.message));
      }

      if (reactSetState) {
        reactSetState(newMessage?.message);
      }
    }
  } catch (error) {
    console.error("Error while reading stream:", error);
    if (setLoadingState) setLoadingState(false);
    return { content: null, chartData: null, sourceDocuments: null };
  }

  try {
    const finalStreamLines = chunks
      .split("\n")
      .filter((streamLine) => streamLine.length > 0);
    const lastCompleteLine = finalStreamLines[finalStreamLines.length - 1];

    if (!lastCompleteLine && chunks.length > 0) {
      console.error("Stream ended without producing a complete JSON object");
    }

    if (lastCompleteLine) {
      let lastMessage;

      try {
        lastMessage = JSON.parse(lastCompleteLine);
      } catch (err) {
        console.error("Error parsing final message:", err);
        throw new Error("Failed to parse the last message", { cause: err });
      }

      if (lastMessage.chart_data) {
        chartData = lastMessage.chart_data;
        lastMessage.message += chartData.content;
      }

      lastMessageValue = lastMessage?.message
        ?.replace(/```/g, ConstantValues.EMPTY_STRING)
        ?.replace(/`/g, ConstantValues.EMPTY_STRING);

      sourceDocuments = lastMessage?.files;
      citations = lastMessage?.citations || [];
      webSearchCitations = lastMessage?.web_search_citations || [];
    }

    if (
      !lastMessageValue ||
      lastMessageValue.trim() === ConstantValues.EMPTY_STRING
    ) {
      lastMessageValue = ERROR_MESSAGES.NO_RESPONSE;
    }
    if (
      !lastMessageValue ||
      lastMessageValue.toLowerCase().includes(ERROR_MESSAGES.LLM_SWITCH_PATTERN)
    ) {
      lastMessageValue += ERROR_MESSAGES.SUPPORT_MESSAGE;
      lastMessageValue = lastMessageValue
        .replace(
          /Try switching to a different llm\./gi,
          ConstantValues.EMPTY_STRING,
        )
        .trim();
    }
  } catch (finalError) {
    console.error("Error processing final chunks:", finalError);
  }

  return {
    content: lastMessageValue,
    citations,
    chartData,
    webSearchCitations,
    sourceDocuments,
    isError: isError,
  };
}
